import type { ReactNode } from 'react'

export interface NavTab {
  to: string
  label: string
  icon: ReactNode
}

/**
 * The primary navigation.
 *
 * The design spec (§4) calls for four tabs — Sheet · Dice · Scene · City. The
 * dice roller is out of scope (see docs/architecture.md §5.8 and §8: the API has
 * no roll resource), so three ship. The nav takes this list as
 * data rather than hard-coding it, so restoring Dice is one entry here.
 */
export const NAV_TABS: NavTab[] = [
  {
    to: '/characters',
    label: 'Sheet',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        aria-hidden="true"
      >
        <path d="M6 3h9l4 4v14H6z" strokeLinejoin="round" />
        <path d="M15 3v5h4M9 12h7M9 16h7" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: '/scene',
    label: 'Scene',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        aria-hidden="true"
      >
        <circle cx="9" cy="8" r="3" />
        <circle cx="16" cy="10" r="2.5" />
        <path
          d="M3.5 20c0-3 2.5-5 5.5-5s5.5 2 5.5 5M15 20c0-2.2 1.7-3.8 4-3.8"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    // Lands on the campaign list, since a city hangs off one campaign. The label stays
    // the spec's, exactly as the Sheet tab keeps its name while landing on the roster.
    to: '/campaigns',
    label: 'City',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        aria-hidden="true"
      >
        <path d="M3 21h18M5 21V9l5-3v15M14 21V11l5-2.5V21" strokeLinejoin="round" />
        <path d="M8 12h.01M8 15.5h.01M17 14h.01M17 17h.01" strokeLinecap="round" />
      </svg>
    ),
  },
]

/**
 * The Live tab — **not** in `NAV_TABS`, because it is conditional.
 *
 * It is appended by `AppShell` only while a session the caller may join is running. A tab that
 * leads to an empty list on most evenings is a tab that costs the other three their width and
 * teaches people to ignore it.
 */
export const LIVE_TAB: NavTab = {
  to: '/live',
  label: 'Live',
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 6h16v12H4z" strokeLinejoin="round" />
      <circle cx="9" cy="12" r="1.6" />
      <circle cx="15" cy="10" r="1.6" />
      <path d="M4 15l4-3 3 2 4-3 5 3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
}
